/**
 * Ceník práce ve výkazu (`docs/infrastructure/Ceny prací výkaz.xlsx`, list „Ceník starý";
 * CSV mirror `docs/infrastructure/csv/Ceny prací výkaz__List1.csv`). List „Ceník nový" zatím ne.
 *
 * Každá **phase** položka se vyhodnotí při přechodu **na** některý z kódů v `phaseCodes`
 * (kódy fází = `WORKFLOWID.PHASE.N`). Workflow jsou lineární; jen BUDOVA má varianty
 * (BUDOVA.2/3), proto je „Zafouknutý" uvedený jako seznam kódů napříč těmito variantami.
 * **milestone** položky se vyhodnotí při dokončení BUDOVA milníku (`milestoneIds`).
 * Bez dalších workflow klíčů — jen fáze / milník + podmínky (povrch, SAP, svaření, HP, …).
 *
 * Podvrt: bez automatické fázové položky, jen materiál.
 */

import type { BudovaMilestoneId } from './object-workflow/budova-milestone-engine';

/** Trigger: fáze, milník BUDOVA, nebo ruční zápis ve výkazu */
export type PriceListTrigger = 'phase' | 'milestone' | 'manual';

/** Jednotka množství pro výpočet ceny */
export type PriceListUnit = 'ks' | 'm' | 'm2' | 'm3' | '1000_kg' | 'h';

/**
 * Jak uživatel zadává hodnotu u ruční položky.
 * - `quantity` (výchozí): množství × `pricePerUnit`
 * - `amount`: zadaná hodnota je přímo částka v Kč (ne ks × sazba)
 */
export type PriceListQuantityMode = 'quantity' | 'amount';

/** Typ podmínky 1 pro phase položky */
export type PriceListConditionType =
  | 'none'
  | 'weld_count_lt_16'
  | 'weld_count_gt_16'
  /** Inclusive 1–16 (alias of corrected `weld_count_lt_16` evaluation). */
  | 'weld_count_lte_16'
  /** Inclusive ≥17 (alias of corrected `weld_count_gt_16` evaluation). */
  | 'weld_count_gte_17'
  | 'surface' // Povrch = ... (excavationMaterial + subtype)
  | 'surface_bucket' // Konsolidovaný billing bucket (ceník nový, m²/m³)
  | 'removal' // Povrch - nad rámec (VYKOPANY): removalBeyondScope
  | 'work_zakopany' // Práce nad rámec (ZAKOPANY): workBeyondScopeZakopany
  | 'restoration_zakopany' // Obnova (ZAKOPANY): restorationZakopany
  | 'sap' // SAP zvoleného materiálu = ...
  | 'sap_not' // SAP zvoleného materiálu ≠ … (žádný z uvedených kódů v materiálu)
  | 'hp_count_4_to_10' // 3 < hpCountTotal < 11 (HP 4–10)
  | 'hp_count_11_to_20' // 10 < hpCountTotal < 21 (HP 11–20)
  | 'hp_count_gt_20' // hpCountTotal > 20 (HP 21+)
  /** Dedikovaná kabeláž (`horizontalniPropoj === true`). Legacy alias: `horizontalni_propoj_s`. */
  | 'kabelaz_dedikovana'
  /** RISER kabeláž (`horizontalniPropoj === false`). Legacy alias: `horizontalni_propoj_bez`. */
  | 'kabelaz_riser'
  /** @deprecated Use `kabelaz_dedikovana` — same meaning (Dedikovaná kabeláž). */
  | 'horizontalni_propoj_s'
  /** @deprecated Use `kabelaz_riser` — same meaning (RISER kabeláž). */
  | 'horizontalni_propoj_bez'
  | 'digitalni_cesta_gt_0'; // digitalniCesta count > 0 (Zřízené VR montáž / Svařený svár)

/** Hodnota podmínky: mapování na kódy v aplikaci */
export type PriceListConditionValue =
  | { type: 'surface'; material: string; subtype?: string } // VOLNY_TEREN+35x70, CHODNIK+ASFALT, KACIREK, VOZOVKA+STERK, deepening
  | { type: 'surface_bucket'; bucket: string; phaseScope?: 'vykop' | 'obnova' }
  | { type: 'removal'; code: string } // CHODNIK_ASFALT, VOZOVKA_ASFALT, BOURANI_BETON_ZAKLADY
  | { type: 'work_zakopany'; code: string } // DLAZBA, MOZAIKA, ASFALT
  | { type: 'restoration_zakopany'; code: string } // BETON, ROZVOZ_ZEMINA
  | { type: 'sap'; sapCodes: string[] };

/** SAP kódy materiálu Matrix (Montáž Matrix) – výkaz při každé změně fáze, pokud je vybrán některý z nich. */
export const WORK_REPORT_MATRIX_MATERIAL_SAP_CODES = [
  '318807',
  '318806',
  '818805',
  '314663',
  '311878',
  '318836',
] as const;

/**
 * SAP kódy HDPE / multiductu (pokládka, 8 Kč/m). Výkaz se tvoří při každé změně fáze,
 * pokud je v `materialUsed` zvolen některý z těchto SAP; množství = součet zadaných metrů
 * (materiály mají jednotku m).
 */
export const WORK_REPORT_POKLADKA_HDPE_SAP_CODES = [
  '300024',
  '300027',
  '300036',
  '300032',
  '324515',
  '325316',
  '325313',
  '327724',
  '325317',
  '325312',
  '324891',
  '325315',
  '325314',
  '327893',
] as const;

/**
 * PE trubka + vrapované / ohebné chráničky — finance skutečnost pro PPD `954970`
 * (Pokládka PE nebo vrapované chráničky) podle spotřebovaného materiálu (m).
 */
export const WORK_REPORT_POKLADKA_PE_VRAPOVANE_SAP_CODES = [
  // PE trubka
  '302637',
  '302655',
  '302672',
  '302752',
  // Vrapovaná (CETIN)
  '302343',
  '302361',
  '302381',
  '302388',
  '302405',
  '302423',
  // FRÄNKISCHE / KOPOS ohebné (katalog Vrapovaná)
  '11.042.737',
  '11.058.470',
  '11.042.736',
  '11.058.471',
  '11.042.732',
  '10.720.030',
  '10.075.343',
  '10.075.671',
  '10.075.304',
  '10.075.308',
  '10.076.051',
] as const;

/**
 * Mikrotrubičky (instalace v budově / digitální cesta) — finance skutečnost pro PPD `958302`
 * (Instalace trubičky v budově). Jen délkové SAP (m), ne spojky/koncovky.
 */
export const WORK_REPORT_INSTALACE_TRUBICKY_SAP_CODES = [
  '300633',
  '300634',
  '300635',
  '300636',
  '300637',
  '323235',
  '323236',
  '323237',
  '323239',
  '323225',
  '323226',
  '317454',
  '317455',
  '317458',
  '320333',
  '300623',
  '300624',
  '300625',
  '300626',
  '300627',
  '300628',
  '300629',
  '300630',
  '300631',
  '300632',
] as const;

export interface WorkReportPriceListItem {
  /** Unikátní kód pro manual položky (dropdown); u phase položek může být prázdný nebo id */
  code: string;
  trigger: PriceListTrigger;
  /** Kódy cílové fáze (`WORKFLOWID.PHASE.N`); seznam = stejná logická fáze napříč variantami */
  phaseCodes: string[];
  /** BUDOVA milníky (`trigger: 'milestone'`) — vyhodnocení při `completeMilestone`. */
  milestoneIds?: readonly BudovaMilestoneId[];
  /**
   * Je-li true, položka se vyhodnotí při každém přechodu do `phaseTo` (když projdou podmínky), ne jen pro výčet phaseCodes.
   * Použití: Montáž Matrix podle SAP v materiálu při libovolné fázi.
   */
  matchAnyPhase?: boolean;
  condition1Type: PriceListConditionType;
  condition1Value?: PriceListConditionValue;
  condition2Type?: PriceListConditionType;
  condition2Value?: PriceListConditionValue;
  activityName: string;
  pricePerUnit: number;
  /** SAP / kód položky práce CETIN (příjem od operátora); viz sloupec „Položky práce CETIN“ v xlsx. */
  cetinSap?: string;
  unit: PriceListUnit;
  /**
   * Ruční zápis: `amount` = uživatel zadá Kč přímo (UI „Částka“).
   * Výchozí / neuvedeno = `quantity` (ks/m/… × sazba).
   */
  quantityMode?: PriceListQuantityMode;
  /**
   * Řádek je vždy srážka: uživatel zadá kladnou Kč, uložená `price` je záporná.
   */
  alwaysNegative?: boolean;
  /** Role (výkres): stavba, montáž, záfuk, optika — volitelný filtr */
  role?: string;
  /** Režim ceníku výkopů: 'linear' (délkové m) nebo 'volume' (m³ / m²). Pokud není uvedeno, platí vždy. */
  excavationPricingMode?: 'linear' | 'volume';
}

/**
 * VYKOP / JAMA — billing výkopu v režimu `volume` (PHASE.1 Vykopaný): konsolidované m²/m³ položky + bourání + HDPE.
 */
export const WORK_REPORT_PHASES_VYKOP_EXCAVATION_BILLING: string[] = [
  'VYKOP.1.PHASE.1',
  'JAMA.1.PHASE.1',
];

/**
 * VYKOP / JAMA — billing obnovy a rozvozu v režimu `volume` / veškerý billing v režimu `linear` (PHASE.2 Finální).
 * Spojkoviště od 2026-05-26 nemá fázi výkopu — do výkazu na povrchy nevstupuje.
 */
export const WORK_REPORT_PHASES_VYKOP_RESTORATION_BILLING: string[] = [
  'VYKOP.1.PHASE.2',
  'JAMA.1.PHASE.2',
];

/** Obnova / Finální billing fáze — alias pro kompatibilitu s existujícím kódem. */
export const WORK_REPORT_PHASES_VYKOP_FINAL_BILLING: string[] = [
  ...WORK_REPORT_PHASES_VYKOP_RESTORATION_BILLING,
];

/** Všechny billing fáze výkopu (PHASE.1 + PHASE.2) — metadata fallback při pricingu. */
export const WORK_REPORT_PHASES_VYKOP_ANY_BILLING: string[] = [
  ...WORK_REPORT_PHASES_VYKOP_EXCAVATION_BILLING,
  ...WORK_REPORT_PHASES_VYKOP_RESTORATION_BILLING,
];

/** Počet sekundárních OK ve specifikaci (`pripojovanyZafukSekundarnichOkPocet`) — položka „Záfuk sekundárních OK“. */
export function readPripojovanyZafukSekundarnichOkPocet(metadata: unknown): number | undefined {
  const m = metadata as Record<string, unknown> | null | undefined;
  if (!m || typeof m !== 'object') return undefined;
  const k = 'pripojovanyZafukSekundarnichOkPocet';
  const cf = (m['customFields'] as Record<string, unknown> | undefined) ?? {};
  const raw = m[k] ?? cf[k];
  if (raw == null || raw === '') return undefined;
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return Math.floor(n);
}

/** ROZVADEC Zafouknutý — Záfuk primárního OK (500 Kč/ks, počet kabelů, ne metráž). */
export const ROZVADEC_ZAFUK_PRIMARNI_OK_LIST_ITEM_CODE = 'rozvadec-zafuk-primarni-ok';
/** ROZVADEC Zafouknutý — Záfuk svazku mikrotrubiček (8 Kč/m, metráž Start–Konec). */
export const ROZVADEC_ZAFUK_SVAZEK_MIKROTRUBICEK_LIST_ITEM_CODE = 'rozvadec-zafuk-svazek-mikrotrubicek';

/** Všechny položky ceníku */
export const WORK_REPORT_PRICE_LIST: WorkReportPriceListItem[] = [
  // Ruční zápis ve výkazu
  { code: 'presun-vykopku', trigger: 'manual', phaseCodes: [], condition1Type: 'none', activityName: 'Přesun výkopku/materiálu po stavbě: výkop - deponie',
    cetinSap: '958843', pricePerUnit: 334, unit: '1000_kg', role: 'stavba' },
  { code: 'hodinova-sazba', trigger: 'manual', phaseCodes: [], condition1Type: 'none', activityName: 'Hodinová sazba po domluvě',
    cetinSap: 'HZS02', pricePerUnit: 250, unit: 'h', role: 'stavba' },
  /**
   * Půjčovna DEK — ruční srážka (export výpisu, řádek před hodinovou sazbou).
   * Uživatel zadá kladnou částku v Kč; systém uloží zápornou cenu (`quantityMode: amount`, `alwaysNegative`).
   * `pricePerUnit: 1` drží paritu výpisu (výkon = částka v Kč).
   */
  { code: 'pujcovna-dek', trigger: 'manual', phaseCodes: [], condition1Type: 'none', activityName: 'Půjčovna DEK',
    cetinSap: 'HZS03', pricePerUnit: 1, unit: 'ks', quantityMode: 'amount', alwaysNegative: true, role: 'stavba' },
  { code: 'akvizice', trigger: 'manual', phaseCodes: [], condition1Type: 'none', activityName: 'Akvizice',
    pricePerUnit: 500, unit: 'ks', role: 'Akvizice' },
  /** Navolávání — ruční položka (stejná role / Excel list Ostatní jako Akvizice). */
  { code: 'navolavani', trigger: 'manual', phaseCodes: [], condition1Type: 'none', activityName: 'Navolávání',
    pricePerUnit: 100, unit: 'ks', role: 'Akvizice' },
  /** Průvrt — jen ruční zápis ve výkazu (ne auto při Zřízené VR). */
  { code: 'pruvrt-beton-do-150mm', trigger: 'manual', phaseCodes: [], condition1Type: 'none',
    activityName: 'Průvrt beton do 150 mm', pricePerUnit: 215, unit: 'ks', role: 'montáž' },

  // Libovolná fáze + Matrix SAP v materiálu
  {
    code: '',
    trigger: 'phase',
    phaseCodes: [],
    matchAnyPhase: true,
    condition1Type: 'sap',
    condition1Value: { type: 'sap', sapCodes: [...WORK_REPORT_MATRIX_MATERIAL_SAP_CODES] },
    activityName: 'Montáž Matrix',
    cetinSap: '955945 + 955946 × 2 + 955948',
    pricePerUnit: 530,
    unit: 'ks',
  },

  // Spojkoviště — Připojené (jen bez Matrix SAP v materiálu; jinak Montáž Matrix)
  {
    code: '',
    trigger: 'phase',
    phaseCodes: ['SPOJKOVISTE.1.PHASE.1'],
    condition1Type: 'sap_not',
    condition1Value: { type: 'sap', sapCodes: [...WORK_REPORT_MATRIX_MATERIAL_SAP_CODES] },
    activityName: 'Montáž multiduct',
    cetinSap: '952582',
    pricePerUnit: 300,
    unit: 'ks',
  },

  // Rozvaděč — Postavený (výkres Montáž / Obecný: 4|1 / 5|1)
  {
    code: 'montaz-rozvadec-postaveny',
    trigger: 'phase',
    phaseCodes: ['ROZVADEC.1.PHASE.1'],
    condition1Type: 'none',
    activityName: 'Montáž rozvaděč',
    cetinSap: '955260',
    pricePerUnit: 500,
    unit: 'ks',
    role: 'montáž',
  },

  // Připojovaný — milník Zavrtaný (Montáž závrty)
  {
    code: '',
    trigger: 'milestone',
    phaseCodes: [],
    milestoneIds: ['ZAVRTANY'],
    condition1Type: 'none',
    activityName: 'Montáž závrty',
    // Ceník: 954944 + 958320 + 955946 × 3 (Gasblock/spojka/koncovky sloučeny do ×3)
    cetinSap: '954944 + 958320 + 955946 × 3',
    pricePerUnit: 725,
    unit: 'ks',
    role: 'montáž',
  },

  // Připojovaný — milník Připravená bábovka (Montáž kruhový kryt; BUDOVA.3 fáze se posouvá automaticky)
  {
    code: 'montaz-kruhovy-kryt',
    trigger: 'milestone',
    phaseCodes: [],
    milestoneIds: ['POSTAVENA_BABOVKA'],
    condition1Type: 'none',
    activityName: 'Montáž kruhový kryt',
    cetinSap: '958727 + 956049 × 15 + 955946 × 2',
    pricePerUnit: 150,
    unit: 'ks',
    role: 'montáž',
  },

  // Připojovaný — Zafouknutý (množství = počet sekundárních OK ve specifikaci; default 1)
  {
    code: 'pripojovany-zafuk-sekundarni-ok',
    trigger: 'phase',
    phaseCodes: ['BUDOVA.2.PHASE.2', 'BUDOVA.3.PHASE.2'],
    condition1Type: 'none',
    activityName: 'Záfuk sekundárních OK',
    cetinSap: '958336',
    pricePerUnit: 500,
    unit: 'ks',
    role: 'záfuk',
  },
  // ROZVADEC — Provařené (PHASE.3). Záfuk PHASE.2: viz položky
  // `rozvadec-zafuk-primarni-ok` (ks) / `rozvadec-zafuk-svazek-mikrotrubicek` (m) na konci ceníku
  // (kódované klíče, aby se neposunuly `pl-{index}` override).
  {
    code: 'montaz-sloupek-pon-do-16-svaru',
    trigger: 'phase',
    phaseCodes: ['ROZVADEC.1.PHASE.3'],
    condition1Type: 'weld_count_lte_16',
    condition2Type: 'sap',
    condition2Value: { type: 'sap', sapCodes: ['324363'] },
    activityName: 'Montáž sloupek PON do 16 svarů',
    cetinSap: '955947 + 958330 × weld_count',
    pricePerUnit: 1000,
    unit: 'ks',
    role: 'optika',
  },
  {
    code: 'montaz-sloupek-pon-nad-16-svaru',
    trigger: 'phase',
    phaseCodes: ['ROZVADEC.1.PHASE.3'],
    condition1Type: 'weld_count_gte_17',
    condition2Type: 'sap',
    condition2Value: { type: 'sap', sapCodes: ['324363'] },
    activityName: 'Montáž sloupek PON nad 16 svarů',
    cetinSap: '955947 + 958330 × weld_count',
    pricePerUnit: 2000,
    unit: 'ks',
  },
  {
    code: 'montaz-os-do-16-svaru',
    trigger: 'phase',
    phaseCodes: ['ROZVADEC.1.PHASE.3'],
    condition1Type: 'weld_count_lte_16',
    condition2Type: 'sap',
    condition2Value: {
      type: 'sap',
      sapCodes: ['325739', '327337', '325737', '319647', '320184', '324995', '325532', '306732', '324994', '320183', '318524', '325765', '325767', '325768', '325764', '325766', '325736', '325760', '325735', '325738'],
    },
    activityName: 'Montáž OS do 16 svarů',
    cetinSap: '955286 + 958330 × weld_count',
    pricePerUnit: 1000,
    unit: 'ks',
  },
  {
    code: 'montaz-os-nad-16-svaru',
    trigger: 'phase',
    phaseCodes: ['ROZVADEC.1.PHASE.3'],
    condition1Type: 'weld_count_gte_17',
    condition2Type: 'sap',
    condition2Value: {
      type: 'sap',
      sapCodes: ['325739', '327337', '325737', '319647', '320184', '324995', '325532', '306732', '324994', '320183', '318524', '325765', '325767', '325768', '325764', '325766', '325736', '325760', '325735', '325738'],
    },
    activityName: 'Montáž OS nad 16 svarů',
    cetinSap: '955286 + 958330 × weld_count',
    pricePerUnit: 2000,
    unit: 'ks',
  },
  {
    code: 'montaz-optika-sloupek-muk-24',
    trigger: 'phase',
    phaseCodes: ['ROZVADEC.1.PHASE.3'],
    condition1Type: 'sap',
    condition1Value: { type: 'sap', sapCodes: ['326274'] },
    activityName: 'Montáž optika sloupek MUK 24',
    cetinSap: '955256 + 958330 × weld_count',
    pricePerUnit: 6000,
    unit: 'ks',
  },
  // 326273 MUK48 — no confirmed auto rule until business confirms (manual only).
  {
    code: 'rozvadec-muk-2x24',
    trigger: 'manual',
    phaseCodes: ['ROZVADEC.1.PHASE.3'],
    condition1Type: 'sap',
    condition1Value: { type: 'sap', sapCodes: ['326273'] },
    activityName: 'Montáž optika kabinet MUK 2×24',
    cetinSap: '955256 + 958330 × weld_count',
    pricePerUnit: 6000,
    unit: 'ks',
  },
  {
    code: 'montaz-optika-1xodf-144vl-na-rsu',
    trigger: 'phase',
    phaseCodes: ['ROZVADEC.1.PHASE.3'],
    condition1Type: 'sap',
    condition1Value: { type: 'sap', sapCodes: ['325715'] },
    activityName: 'Montáž optika 1xODF 144vl na RSU',
    cetinSap: '955260 + 958330 × weld_count',
    pricePerUnit: 6000,
    unit: 'ks',
  },

  // Připojovaný — Svařený (SAP) — Placeholder ve fakturaci; CETIN vzorec z ceníku
  {
    code: '',
    trigger: 'phase',
    phaseCodes: ['BUDOVA.2.PHASE.3'],
    condition1Type: 'sap',
    condition1Value: { type: 'sap', sapCodes: ['325223', '325224', '325225', '327108', '325632', '325235'] },
    activityName: 'Montáž a svár PB VR do 53BJ',
    cetinSap: '958720 + 958330 × weld_count',
    pricePerUnit: 1500,
    unit: 'ks',
  },
  {
    code: '',
    trigger: 'phase',
    phaseCodes: ['BUDOVA.2.PHASE.3'],
    condition1Type: 'sap',
    condition1Value: { type: 'sap', sapCodes: ['325226'] },
    activityName: 'Montáž a svár PB VR nad 54 BJ',
    cetinSap: '958720 + 958330 × weld_count',
    pricePerUnit: 2000,
    unit: 'ks',
  },
  // Připojovaný (MSO) — Svařený: svár digitální cesty (ks = počet z pripojovany_objekt_spec.digitalni_cesta)
  {
    code: 'svar-digitalni-cesty',
    trigger: 'phase',
    phaseCodes: ['BUDOVA.2.PHASE.3'],
    condition1Type: 'digitalni_cesta_gt_0',
    activityName: 'Svár digitální cesty',
    pricePerUnit: 120,
    unit: 'ks',
    role: 'optika',
  },

  // Vykopaný — povrch (délka m)
  {
    code: '',
    trigger: 'phase',
    phaseCodes: [...WORK_REPORT_PHASES_VYKOP_FINAL_BILLING],
    condition1Type: 'surface',
    condition1Value: { type: 'surface', material: 'VOLNY_TEREN', subtype: '35x70' },
    activityName: 'Výkopové práce volný terén 35x70 cm',
    cetinSap: '952354',
    pricePerUnit: 250,
    unit: 'm',
    role: 'stavba',
  },
  {
    code: '',
    trigger: 'phase',
    phaseCodes: [...WORK_REPORT_PHASES_VYKOP_FINAL_BILLING],
    condition1Type: 'surface',
    condition1Value: { type: 'surface', material: 'VOLNY_TEREN', subtype: '35x100' },
    activityName: 'Výkopové práce volný terén 35x100 cm',
    cetinSap: '952353',
    pricePerUnit: 300,
    unit: 'm',
  },
  {
    code: '',
    trigger: 'phase',
    phaseCodes: [...WORK_REPORT_PHASES_VYKOP_FINAL_BILLING],
    condition1Type: 'surface',
    condition1Value: { type: 'surface', material: 'DEEPENING' }, // Prohloubení výkopu – speciální
    activityName: 'Prohloubení výkopu o 10 cm',
    cetinSap: '955551',
    pricePerUnit: 25,
    unit: 'm',
    role: 'stavba',
  },
  {
    code: '',
    trigger: 'phase',
    phaseCodes: [...WORK_REPORT_PHASES_VYKOP_FINAL_BILLING],
    condition1Type: 'surface',
    condition1Value: { type: 'surface', material: 'CHODNIK', subtype: 'ASFALT' },
    activityName: 'Výkop chodník asfalt - beton 35x50 cm',
    cetinSap: '954951',
    pricePerUnit: 288,
    unit: 'm',
  },
  {
    code: '',
    trigger: 'phase',
    phaseCodes: [...WORK_REPORT_PHASES_VYKOP_FINAL_BILLING],
    condition1Type: 'surface',
    condition1Value: { type: 'surface', material: 'CHODNIK', subtype: 'BETON' },
    activityName: 'Výkop chodník asfalt - beton 35x50 cm',
    cetinSap: '954951',
    pricePerUnit: 288,
    unit: 'm',
  },
  {
    code: '',
    trigger: 'phase',
    phaseCodes: [...WORK_REPORT_PHASES_VYKOP_FINAL_BILLING],
    condition1Type: 'surface',
    condition1Value: { type: 'surface', material: 'CHODNIK', subtype: 'DLAZBA' },
    activityName: 'Výkop chodník zámková a ostatní dlažba 35x50 cm',
    cetinSap: '952362',
    pricePerUnit: 285,
    unit: 'm',
  },
  {
    code: '',
    trigger: 'phase',
    phaseCodes: [...WORK_REPORT_PHASES_VYKOP_FINAL_BILLING],
    condition1Type: 'surface',
    condition1Value: { type: 'surface', material: 'CHODNIK', subtype: 'MOZAIKA' },
    activityName: 'Výkop chodník mozaika 35x50 cm',
    cetinSap: '952361',
    pricePerUnit: 313,
    unit: 'm',
  },
  {
    code: '',
    trigger: 'phase',
    phaseCodes: [...WORK_REPORT_PHASES_VYKOP_FINAL_BILLING],
    condition1Type: 'surface',
    condition1Value: { type: 'surface', material: 'KACIREK' },
    activityName: 'Výkop kačírek (celá kyneta) 35.65 cm',
    cetinSap: '955577',
    pricePerUnit: 300,
    unit: 'm',
  },
  {
    code: '',
    trigger: 'phase',
    phaseCodes: [...WORK_REPORT_PHASES_VYKOP_FINAL_BILLING],
    condition1Type: 'surface',
    condition1Value: { type: 'surface', material: 'VOZOVKA', subtype: 'ASFALT' },
    activityName: 'Výkop vozovka asfalt a beton 40x110cm povrch do šířky 60cm',
    cetinSap: '955570',
    pricePerUnit: 663,
    unit: 'm',
  },
  {
    code: '',
    trigger: 'phase',
    phaseCodes: [...WORK_REPORT_PHASES_VYKOP_FINAL_BILLING],
    condition1Type: 'surface',
    condition1Value: { type: 'surface', material: 'VOZOVKA', subtype: 'BETON' },
    activityName: 'Výkop vozovka asfalt a beton 40x110cm povrch do šířky 60cm',
    cetinSap: '955570',
    pricePerUnit: 663,
    unit: 'm',
  },
  {
    code: '',
    trigger: 'phase',
    phaseCodes: [...WORK_REPORT_PHASES_VYKOP_FINAL_BILLING],
    condition1Type: 'surface',
    condition1Value: { type: 'surface', material: 'VOZOVKA', subtype: 'DLAZBA' },
    activityName: 'Výkop vozovka dlažba zámková mozaika 40x110cm povrch do šířky 60cm (rozebrání - složení)',
    cetinSap: '955820',
    pricePerUnit: 484,
    unit: 'm',
  },
  {
    code: '',
    trigger: 'phase',
    phaseCodes: [...WORK_REPORT_PHASES_VYKOP_FINAL_BILLING],
    condition1Type: 'surface',
    condition1Value: { type: 'surface', material: 'VOZOVKA', subtype: 'STERK' },
    activityName: 'Výkop vozovka šteřkový povrch asfaltová drť 40x110cm',
    cetinSap: '958327',
    pricePerUnit: 488,
    unit: 'm',
  },

  // Vykopaný — odstranění nad rámec
  {
    code: '',
    trigger: 'phase',
    phaseCodes: [...WORK_REPORT_PHASES_VYKOP_FINAL_BILLING],
    condition1Type: 'removal',
    condition1Value: { type: 'removal', code: 'CHODNIK_ASFALT' },
    activityName: 'Ostranění nad rámec chodník asfalt',
    cetinSap: '954973',
    pricePerUnit: 222,
    unit: 'm2',
  },
  {
    code: '',
    trigger: 'phase',
    phaseCodes: [...WORK_REPORT_PHASES_VYKOP_FINAL_BILLING],
    condition1Type: 'removal',
    condition1Value: { type: 'removal', code: 'VOZOVKA_ASFALT' },
    activityName: 'Ostranění nad rámec vozovka asfalt',
    cetinSap: '958777',
    pricePerUnit: 194,
    unit: 'm2',
  },
  {
    code: '',
    trigger: 'phase',
    phaseCodes: [...WORK_REPORT_PHASES_VYKOP_FINAL_BILLING],
    condition1Type: 'removal',
    condition1Value: { type: 'removal', code: 'BOURANI_BETON_ZAKLADY' },
    activityName: 'Bourání betonových základů',
    cetinSap: '951576',
    pricePerUnit: 900,
    unit: 'm3',
  },

  // Finální / zakopání — práce nad rámec, obnova
  {
    code: '',
    trigger: 'phase',
    phaseCodes: [...WORK_REPORT_PHASES_VYKOP_FINAL_BILLING],
    condition1Type: 'work_zakopany',
    condition1Value: { type: 'work_zakopany', code: 'DLAZBA' },
    activityName: 'Obnova nad rámec chodník - vozovka - zámková dlažba (rozebrání - složení)',
    cetinSap: '954974',
    pricePerUnit: 290,
    unit: 'm2',
  },
  {
    code: '',
    trigger: 'phase',
    phaseCodes: [...WORK_REPORT_PHASES_VYKOP_FINAL_BILLING],
    condition1Type: 'work_zakopany',
    condition1Value: { type: 'work_zakopany', code: 'MOZAIKA' },
    activityName: 'Obnova nad rámec chodník - vozovka - mozaika (rozebrání - složení)',
    cetinSap: '954975',
    pricePerUnit: 350,
    unit: 'm2',
  },
  {
    code: '',
    trigger: 'phase',
    phaseCodes: [...WORK_REPORT_PHASES_VYKOP_FINAL_BILLING],
    condition1Type: 'work_zakopany',
    condition1Value: { type: 'work_zakopany', code: 'ASFALT' },
    activityName: 'Obnova nad rámec chodník - vozovka - asfalt',
    cetinSap: '954976',
    pricePerUnit: 200,
    unit: 'm2',
  },
  {
    code: '',
    trigger: 'phase',
    phaseCodes: [...WORK_REPORT_PHASES_VYKOP_FINAL_BILLING],
    condition1Type: 'restoration_zakopany',
    condition1Value: { type: 'restoration_zakopany', code: 'BETON' },
    activityName: 'Obnova betonu síla do 20 cm',
    cetinSap: '954976',
    pricePerUnit: 225,
    unit: 'm2',
  },
  {
    code: '',
    trigger: 'phase',
    phaseCodes: [...WORK_REPORT_PHASES_VYKOP_FINAL_BILLING],
    condition1Type: 'removal',
    condition1Value: { type: 'removal', code: 'ROZVOZ_ZEMINA' },
    activityName: 'Rozvoz kolečkem zemina',
    cetinSap: 'HZS01',
    pricePerUnit: 139,
    unit: 'm2',
  },

  // Připojovaný — milník Zřízené VR: Výstavba vnitřních rozvodů (HP rozsah)
  {
    code: 'vystavba-vr-4-10hp',
    trigger: 'milestone',
    phaseCodes: [],
    milestoneIds: ['ZRIZENE_VR'],
    condition1Type: 'hp_count_4_to_10',
    activityName: 'Výstavba vnitřních rozvodů 4 - 10 HP',
    // Základní SAP; HP×RISER/DEDIKOVANA se ve finance rozkladu řeší zvlášť (proměnné).
    cetinSap: '958716',
    pricePerUnit: 2690,
    unit: 'ks',
  },
  {
    code: 'vystavba-vr-11-20hp',
    trigger: 'milestone',
    phaseCodes: [],
    milestoneIds: ['ZRIZENE_VR'],
    condition1Type: 'hp_count_11_to_20',
    activityName: 'Výstavba vnitřních rozvodů 11 - 20 HP',
    cetinSap: '958717',
    pricePerUnit: 5380,
    unit: 'ks',
  },
  {
    code: 'vystavba-vr-21hp-plus',
    trigger: 'milestone',
    phaseCodes: [],
    milestoneIds: ['ZRIZENE_VR'],
    condition1Type: 'hp_count_gt_20',
    activityName: 'Výstavba vnitřních rozvodů 21 HP a více',
    cetinSap: '958718',
    pricePerUnit: 8070,
    unit: 'ks',
  },

  // Připojovaný — milník Zřízené VR: Dokumentace + bonus Výstavba podle typu kabeláže (RISER / Dedikovaná)
  {
    code: 'dokumentace-vystavba-vr-vertikala',
    trigger: 'milestone',
    phaseCodes: [],
    milestoneIds: ['ZRIZENE_VR'],
    condition1Type: 'kabelaz_riser',
    activityName: 'Dokumentace výstavba vnitřních rozvodů RISER kabeláž',
    cetinSap: '958723',
    pricePerUnit: 900,
    unit: 'ks',
  },
  {
    code: 'dokumentace-vystavba-vr-vertikala-horizontala',
    trigger: 'milestone',
    phaseCodes: [],
    milestoneIds: ['ZRIZENE_VR'],
    condition1Type: 'kabelaz_dedikovana',
    activityName: 'Dokumentace výstavba vnitřních rozvodů Dedikovaná kabeláž',
    cetinSap: '958723',
    pricePerUnit: 1200,
    unit: 'ks',
  },
  {
    code: 'vystavba-vr-vertikala-horizontala',
    trigger: 'milestone',
    phaseCodes: [],
    milestoneIds: ['ZRIZENE_VR'],
    condition1Type: 'kabelaz_dedikovana',
    activityName: 'Výstavba vnitřních rozvodů Dedikovaná kabeláž',
    // CETIN 958708 = „Rozvody opt. pro 1 byt" → quantity = hpCountTotal (ks = byty/HP).
    cetinSap: '958708',
    pricePerUnit: 900,
    unit: 'ks',
  },

  // Připojovaný — milník Zřízené VR: Montáž digitální cesta (ks = počet digitálních cest)
  {
    code: 'montaz-digitalni-cesta',
    trigger: 'milestone',
    phaseCodes: [],
    milestoneIds: ['ZRIZENE_VR'],
    condition1Type: 'digitalni_cesta_gt_0',
    activityName: 'Montáž digitální cesta',
    pricePerUnit: 500,
    unit: 'ks',
    role: 'montáž',
  },

  /**
   * Lištování nad 10 m — jen ruční zápis ve výkazu (metry zadá uživatel).
   * Pozice v poli je záměrně původní (milníková): `code` drží override klíč,
   * přesun by přečísloval `pl-{index}` u bezkódových položek pod ním.
   */
  {
    code: 'listovani-nad-10m',
    trigger: 'manual',
    phaseCodes: [],
    condition1Type: 'none',
    activityName: 'Lištování nad 10 m',
    cetinSap: '958719',
    pricePerUnit: 70,
    unit: 'm',
    role: 'montáž',
  },

  // Pokládka HDPE / multiduct — podle SAP zvoleného materiálu, nezávisle na fázi.
  // Množství (m) = součet `quantity` materiálů s odpovídajícím SAP v `materialUsed` (jednotka m).
  // Kód: stabilní klíč pro override cen + detekce ve `WorkReportPricingService.appendPricedRow`,
  // kde se do `activityName` zapíšou i vypočtené metry („Pokládka HDPE, multiduct – 12 m“).
  {
    code: 'pokladka-hdpe-multiduct',
    trigger: 'phase',
    phaseCodes: [],
    matchAnyPhase: true,
    condition1Type: 'sap',
    condition1Value: { type: 'sap', sapCodes: [...WORK_REPORT_POKLADKA_HDPE_SAP_CODES] },
    activityName: 'Pokládka HDPE, multiduct',
    cetinSap: '952602',
    pricePerUnit: 8,
    unit: 'm',
  },

  // ROZVADEC — Zafouknutý (PHASE.2): primární OK je ks (počet kabelů s platnou metráží),
  // svazek mikrotrubiček zůstává v metrech (|konec − start|). Ne material_usage.
  // Na konci pole, aby se neposunuly `pl-{index}` u bezkódových položek.
  {
    code: ROZVADEC_ZAFUK_PRIMARNI_OK_LIST_ITEM_CODE,
    trigger: 'phase',
    phaseCodes: ['ROZVADEC.1.PHASE.2'],
    condition1Type: 'none',
    activityName: 'Záfuk primárního OK',
    cetinSap: '958336',
    pricePerUnit: 500,
    unit: 'ks',
    role: 'záfuk',
  },
  {
    code: ROZVADEC_ZAFUK_SVAZEK_MIKROTRUBICEK_LIST_ITEM_CODE,
    trigger: 'phase',
    phaseCodes: ['ROZVADEC.1.PHASE.2'],
    condition1Type: 'none',
    activityName: 'Záfuk svazku mikrotrubiček',
    cetinSap: '955942',
    pricePerUnit: 8,
    unit: 'm',
    role: 'záfuk',
  },

  // =========================================================================
  // Ceník nový (režim `volume` — m³ / m²): Výkop PHASE.1 + Obnova PHASE.2
  // Umístěno na konci s explicitními `code` klíči, aby se neposunuly `pl-{index}` u stávajících položek.
  // =========================================================================

  // VYKOP PHASE.1 — výkop (konsolidované buckety, m²/m³)
  {
    code: 'vykop-m3-volny-teren',
    trigger: 'phase',
    phaseCodes: [...WORK_REPORT_PHASES_VYKOP_EXCAVATION_BILLING],
    condition1Type: 'surface_bucket',
    condition1Value: { type: 'surface_bucket', bucket: 'VOLNY_TEREN', phaseScope: 'vykop' },
    activityName: 'Výkop volný terén',
    pricePerUnit: 700,
    unit: 'm3',
    role: 'stavba',
    excavationPricingMode: 'volume',
  },
  {
    code: 'vykop-m2-asfalt-beton',
    trigger: 'phase',
    phaseCodes: [...WORK_REPORT_PHASES_VYKOP_EXCAVATION_BILLING],
    condition1Type: 'surface_bucket',
    condition1Value: { type: 'surface_bucket', bucket: 'ASFALT_A_BETON', phaseScope: 'vykop' },
    activityName: 'Výkop asfalt a beton',
    pricePerUnit: 210,
    unit: 'm2',
    role: 'stavba',
    excavationPricingMode: 'volume',
  },
  {
    code: 'vykop-m2-dlazba',
    trigger: 'phase',
    phaseCodes: [...WORK_REPORT_PHASES_VYKOP_EXCAVATION_BILLING],
    condition1Type: 'surface_bucket',
    condition1Value: { type: 'surface_bucket', bucket: 'DLAZBA', phaseScope: 'vykop' },
    activityName: 'Výkop zámková a ostatní dlažba',
    pricePerUnit: 90,
    unit: 'm2',
    role: 'stavba',
    excavationPricingMode: 'volume',
  },
  {
    code: 'vykop-m2-mozaika',
    trigger: 'phase',
    phaseCodes: [...WORK_REPORT_PHASES_VYKOP_EXCAVATION_BILLING],
    condition1Type: 'surface_bucket',
    condition1Value: { type: 'surface_bucket', bucket: 'MOZAIKA', phaseScope: 'vykop' },
    activityName: 'Výkop mozaika',
    pricePerUnit: 90,
    unit: 'm2',
    role: 'stavba',
    excavationPricingMode: 'volume',
  },
  {
    code: 'vykop-m2-kacirek',
    trigger: 'phase',
    phaseCodes: [...WORK_REPORT_PHASES_VYKOP_EXCAVATION_BILLING],
    condition1Type: 'surface_bucket',
    condition1Value: { type: 'surface_bucket', bucket: 'KACIREK', phaseScope: 'vykop' },
    activityName: 'Výkop kačírek',
    pricePerUnit: 90,
    unit: 'm2',
    role: 'stavba',
    excavationPricingMode: 'volume',
  },
  {
    code: 'vykop-m2-sterk',
    trigger: 'phase',
    phaseCodes: [...WORK_REPORT_PHASES_VYKOP_EXCAVATION_BILLING],
    condition1Type: 'surface_bucket',
    condition1Value: { type: 'surface_bucket', bucket: 'STERK', phaseScope: 'vykop' },
    activityName: 'Výkop štěrk a asfaltová drť',
    pricePerUnit: 115,
    unit: 'm2',
    role: 'stavba',
    excavationPricingMode: 'volume',
  },
  {
    code: 'vykop-m3-bourani-beton',
    trigger: 'phase',
    phaseCodes: [...WORK_REPORT_PHASES_VYKOP_EXCAVATION_BILLING],
    condition1Type: 'removal',
    condition1Value: { type: 'removal', code: 'BOURANI_BETON_ZAKLADY' },
    activityName: 'Bourání betonových základů',
    cetinSap: '951576',
    pricePerUnit: 900,
    unit: 'm3',
    role: 'stavba',
    excavationPricingMode: 'volume',
  },

  // VYKOP PHASE.2 — obnova (konsolidované buckety, m²/m³)
  {
    code: 'obnova-m3-volny-teren',
    trigger: 'phase',
    phaseCodes: [...WORK_REPORT_PHASES_VYKOP_RESTORATION_BILLING],
    condition1Type: 'surface_bucket',
    condition1Value: { type: 'surface_bucket', bucket: 'VOLNY_TEREN', phaseScope: 'obnova' },
    activityName: 'Obnova volný terén',
    pricePerUnit: 325,
    unit: 'm3',
    role: 'stavba',
    excavationPricingMode: 'volume',
  },
  {
    code: 'obnova-m2-asfalt-beton',
    trigger: 'phase',
    phaseCodes: [...WORK_REPORT_PHASES_VYKOP_RESTORATION_BILLING],
    condition1Type: 'surface_bucket',
    condition1Value: { type: 'surface_bucket', bucket: 'ASFALT_A_BETON', phaseScope: 'obnova' },
    activityName: 'Obnova asfalt a beton',
    pricePerUnit: 210,
    unit: 'm2',
    role: 'stavba',
    excavationPricingMode: 'volume',
  },
  {
    code: 'obnova-m2-dlazba',
    trigger: 'phase',
    phaseCodes: [...WORK_REPORT_PHASES_VYKOP_RESTORATION_BILLING],
    condition1Type: 'surface_bucket',
    condition1Value: { type: 'surface_bucket', bucket: 'DLAZBA', phaseScope: 'obnova' },
    activityName: 'Obnova zámková a ostatní dlažba',
    pricePerUnit: 225,
    unit: 'm2',
    role: 'stavba',
    excavationPricingMode: 'volume',
  },
  {
    code: 'obnova-m2-mozaika',
    trigger: 'phase',
    phaseCodes: [...WORK_REPORT_PHASES_VYKOP_RESTORATION_BILLING],
    condition1Type: 'surface_bucket',
    condition1Value: { type: 'surface_bucket', bucket: 'MOZAIKA', phaseScope: 'obnova' },
    activityName: 'Obnova mozaika',
    pricePerUnit: 300,
    unit: 'm2',
    role: 'stavba',
    excavationPricingMode: 'volume',
  },
  {
    code: 'obnova-m2-kacirek',
    trigger: 'phase',
    phaseCodes: [...WORK_REPORT_PHASES_VYKOP_RESTORATION_BILLING],
    condition1Type: 'surface_bucket',
    condition1Value: { type: 'surface_bucket', bucket: 'KACIREK', phaseScope: 'obnova' },
    activityName: 'Obnova kačírek',
    pricePerUnit: 110,
    unit: 'm2',
    role: 'stavba',
    excavationPricingMode: 'volume',
  },
  {
    code: 'obnova-m2-sterk',
    trigger: 'phase',
    phaseCodes: [...WORK_REPORT_PHASES_VYKOP_RESTORATION_BILLING],
    condition1Type: 'surface_bucket',
    condition1Value: { type: 'surface_bucket', bucket: 'STERK', phaseScope: 'obnova' },
    activityName: 'Obnova štěrk a asfaltová drť',
    pricePerUnit: 170,
    unit: 'm2',
    role: 'stavba',
    excavationPricingMode: 'volume',
  },
  {
    code: 'obnova-m3-rozvoz-zemina',
    trigger: 'phase',
    phaseCodes: [...WORK_REPORT_PHASES_VYKOP_RESTORATION_BILLING],
    condition1Type: 'removal',
    condition1Value: { type: 'removal', code: 'ROZVOZ_ZEMINA' },
    activityName: 'Rozvoz kolečkem zemina',
    cetinSap: 'HZS01',
    pricePerUnit: 400,
    unit: 'm3',
    role: 'stavba',
    excavationPricingMode: 'volume',
  },
];

/** Jednotka „Kč“ v PPD rekapitulaci není množstevní jednotka — v UI zobrazujeme ks / m / … */
export function isFinanceMoneyUnit(unit: string | null | undefined): boolean {
  if (!unit?.trim()) return false;
  const u = unit
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  return u === 'kc';
}

/** Zkrácený popisek množstevní jednotky pro finance / ceník (ks, m, m², m³, hod, t). */
export function displayWorkQuantityUnit(unit: string): string {
  switch (unit.trim()) {
    case '1000_kg':
      return 't';
    case 'h':
      return 'hod';
    case 'm2':
      return 'm²';
    case 'm3':
      return 'm³';
    default:
      return unit.trim();
  }
}

/**
 * Jednotka pro sloupec množství u finance srovnání prací.
 * PPD rekapitulace ukládá `Kč`; doplní se z ceníku výkazu nebo Protelco katalogu.
 */
export function resolveWorkFinanceUnit(
  sap: string,
  unit: string | null | undefined,
  protelcoUnit?: string | null,
): string | null {
  if (unit?.trim() && !isFinanceMoneyUnit(unit)) {
    return displayWorkQuantityUnit(unit);
  }

  const key = sap.trim();
  if (key) {
    for (const item of WORK_REPORT_PRICE_LIST) {
      const cetin = item.cetinSap?.trim();
      if (!cetin) continue;
      const aggKey = normalizeWorkCetinSapKey(cetin, item.activityName);
      if (aggKey === key || (/^\d+$/.test(cetin) && cetin === key)) {
        return displayWorkQuantityUnit(item.unit);
      }
    }
  }

  if (protelcoUnit?.trim() && !isFinanceMoneyUnit(protelcoUnit)) {
    return displayWorkQuantityUnit(protelcoUnit);
  }

  return null;
}

/** Klíč pro agregaci finance srovnání práce (PPD vs výkaz). */
export function normalizeWorkCetinSapKey(cetinSap: string, activityName?: string): string {
  const raw = cetinSap.trim();
  if (/^\d+$/.test(raw)) return raw;
  if (/^HZS\d+$/i.test(raw)) return raw.toUpperCase();
  const act = activityName?.trim();
  if (act) return `act:${act}`;
  const m = raw.match(/(\d{5,6})/);
  return m ? m[1] : raw;
}

/** Najde položku ceníku podle názvu činnosti (přesná shoda, prefix, nebo metadata.listItemCode). */
export function findWorkReportPriceListItem(
  activityName: string,
  listItemCode?: string | null,
): WorkReportPriceListItem | undefined {
  const code = listItemCode?.trim();
  if (code) {
    const byCode = WORK_REPORT_PRICE_LIST.find((item) => item.code === code);
    if (byCode) return byCode;
  }
  const name = activityName.trim();
  if (!name) return undefined;
  const exact = WORK_REPORT_PRICE_LIST.find((item) => item.activityName === name);
  if (exact) return exact;
  return WORK_REPORT_PRICE_LIST.find(
    (item) => name.startsWith(item.activityName) || item.activityName.startsWith(name),
  );
}

/** BUDOVA milníky, které generují automatický výkaz práce. */
export const BUDOVA_WORK_REPORT_BILLING_MILESTONE_IDS = [
  'ZAVRTANY',
  'ZRIZENE_VR',
  'POSTAVENA_BABOVKA',
] as const satisfies readonly BudovaMilestoneId[];

/** Položky ceníku vázané na dokončení BUDOVA milníku. */
export function getWorkReportPriceListItemsForMilestone(
  milestoneId: BudovaMilestoneId,
): WorkReportPriceListItem[] {
  return WORK_REPORT_PRICE_LIST.filter(
    (item) => item.trigger === 'milestone' && item.milestoneIds?.includes(milestoneId),
  );
}

/** Položky pro dropdown v záložce Výkaz práce (Přidat položku) */
export function getManualPriceListItems(): WorkReportPriceListItem[] {
  return WORK_REPORT_PRICE_LIST.filter((item) => item.trigger === 'manual' && item.code);
}

/** Položka podle kódu (pro ruční záznam) */
export function getManualItemByCode(code: string): WorkReportPriceListItem | undefined {
  return WORK_REPORT_PRICE_LIST.find((item) => item.trigger === 'manual' && item.code === code);
}

/** Ruční položka se zadává jako částka v Kč (ne množství × sazba). */
export function isAmountModePriceListItem(
  item: Pick<WorkReportPriceListItem, 'quantityMode'> | null | undefined,
): boolean {
  return item?.quantityMode === 'amount';
}

/**
 * Cena ručního řádku ze zadané hodnoty (množství nebo Kč).
 * U `quantityMode: 'amount'` je vstup přímo Kč; jinak `abs(vstup) * pricePerUnit`.
 * U `alwaysNegative`: vždy `price = -Math.abs(...)` (1500 i -1500 → -1500).
 */
export function computeManualWorkReportLinePrice(
  item: WorkReportPriceListItem,
  enteredAmount: number,
): number | null {
  if (!Number.isFinite(enteredAmount) || enteredAmount === 0) return null;
  const abs = Math.abs(enteredAmount);
  const raw =
    item.quantityMode === 'amount'
      ? Math.round(abs * 100) / 100
      : Math.round(abs * item.pricePerUnit * 100) / 100;
  if (!Number.isFinite(raw) || raw <= 0) return null;
  return item.alwaysNegative ? -Math.abs(raw) : raw;
}

/**
 * Stabilní klíč pro položku ceníku (override cen na polygonu).
 * Ruční položky: `code`; fázové bez kódu: `pl-{index}` v pořadí `WORK_REPORT_PRICE_LIST`.
 */
export function getWorkReportPriceListItemKey(item: WorkReportPriceListItem, index: number): string {
  const c = (item.code ?? '').trim();
  return c.length > 0 ? c : `pl-${index}`;
}

/** Řádek pro UI / API – všechny položky s naceněním */
export interface WorkReportPriceCatalogRow {
  priceListId: string;
  activityName: string;
  pricePerUnit: number;
  /** SAP / kód položky práce CETIN (příjem od operátora); viz sloupec „Položky práce CETIN“ v xlsx. */
  cetinSap?: string;
  unit: PriceListUnit;
  trigger: PriceListTrigger;
  /** Režim ceníku výkopů ('linear' = délkové m, 'volume' = m³ / m²), nebo undefined pro ostatní položky. */
  excavationPricingMode?: 'linear' | 'volume';
}

function inferCatalogRowExcavationPricingMode(
  item: WorkReportPriceListItem,
): 'linear' | 'volume' | undefined {
  if (item.excavationPricingMode) return item.excavationPricingMode;
  if (
    item.condition1Type === 'surface' ||
    item.condition1Type === 'work_zakopany' ||
    item.condition1Type === 'restoration_zakopany' ||
    item.condition1Type === 'removal'
  ) {
    return 'linear';
  }
  return undefined;
}

/** Kompletní ceník (globální výchozí ceny z konstant) */
export function getWorkReportPriceListCatalog(
  options?: { excavationPricingMode?: 'linear' | 'volume' | 'all' },
): WorkReportPriceCatalogRow[] {
  const modeFilter = options?.excavationPricingMode;
  return WORK_REPORT_PRICE_LIST.map((item, index) => {
    const excavationPricingMode = inferCatalogRowExcavationPricingMode(item);
    return {
      priceListId: getWorkReportPriceListItemKey(item, index),
      activityName: item.activityName,
      pricePerUnit: item.pricePerUnit,
      cetinSap: item.cetinSap,
      unit: item.unit,
      trigger: item.trigger,
      ...(excavationPricingMode ? { excavationPricingMode } : {}),
    };
  }).filter((row) => {
    if (!modeFilter || modeFilter === 'all') return true;
    if (!row.excavationPricingMode) return true;
    return row.excavationPricingMode === modeFilter;
  });
}
